import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import { login, verifyAdminOTP, resendAdminOTP } from '../services/api.ts';
import { Lock, Mail, Eye, EyeOff, KeyRound, ArrowLeft, RefreshCw } from 'lucide-react';
import './Login.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // OTP state
  const [showOTP, setShowOTP] = useState(false);
  const [superuserId, setSuperuserId] = useState<number | null>(null);
  const [emailHint, setEmailHint] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Attempting login with:', usernameOrEmail);
      
      const response = await login(usernameOrEmail, password);
      
      console.log('Login response received:', response);
      
      // Check if OTP is required
      if (response.otp_required) {
        setSuperuserId(response.superuser_id || null);
        setEmailHint(response.email_hint || '');
        setShowOTP(true);
        setResendCooldown(60);
        setOtpDigits(['', '', '', '', '', '']);
        // Focus first OTP input
        setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
      } else if (response.status === 'success' && response.user && response.token) {
        // OTP disabled - direct login with JWT
        const authUser = {
          id: response.user.id.toString(),
          username: response.user.username,
          email: response.user.email,
          role_name: response.user.role_name || 'Unknown',
          permissions: response.user.permissions || [],
        };
        authLogin(response.token, authUser);
        navigate('/dashboard');
      } else {
        setError(response.message || 'Login failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.detail 
        || err.response?.data?.message 
        || err.message 
        || 'Login failed. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    
    // Auto-focus next input
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits entered
    if (digit && index === 5 && newDigits.every(d => d !== '')) {
      handleVerifyOTP(newDigits.join(''));
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newDigits = pastedData.split('');
      setOtpDigits(newDigits);
      otpInputRefs.current[5]?.focus();
      handleVerifyOTP(pastedData);
    }
  };

  const handleVerifyOTP = async (code?: string) => {
    const otpCode = code || otpDigits.join('');
    if (otpCode.length !== 6 || !superuserId) return;
    
    setError('');
    setVerifying(true);

    try {
      const response = await verifyAdminOTP(superuserId, otpCode);
      
      if (response.status === 'success' && response.user && response.token) {
        const authUser = {
          id: response.user.id.toString(),
          username: response.user.username,
          email: response.user.email,
          role_name: response.user.role_name || 'Unknown',
          permissions: response.user.permissions || [],
        };
        authLogin(response.token, authUser);
        navigate('/dashboard');
      } else {
        setError(response.message || 'Verification failed.');
      }
    } catch (err: any) {
      console.error('OTP verification error:', err);
      const errorMessage = err.response?.data?.detail 
        || err.response?.data?.message 
        || 'Verification failed. Please try again.';
      setError(errorMessage);
      // Clear OTP on error
      setOtpDigits(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0 || !superuserId) return;
    
    setError('');
    setResending(true);

    try {
      const response = await resendAdminOTP(superuserId);
      setEmailHint(response.email_hint);
      setResendCooldown(60);
      setOtpDigits(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } catch (err: any) {
      console.error('Resend OTP error:', err);
      const errorMessage = err.response?.data?.detail 
        || err.response?.data?.message 
        || 'Failed to resend code. Please try again.';
      setError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setShowOTP(false);
    setSuperuserId(null);
    setEmailHint('');
    setOtpDigits(['', '', '', '', '', '']);
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="gradient-circle circle-1"></div>
        <div className="gradient-circle circle-2"></div>
        <div className="gradient-circle circle-3"></div>
      </div>

      <div className="login-container">
        <div className="login-card">
          {!showOTP ? (
            // Login Form
            <>
              <div className="login-header">
                <div className="logo-wrapper">
                  <div className="logo-icon">
                    <img src="/logo.png" alt="PriceCheck" className="logo-img" />
                  </div>
                </div>
                <h1>Welcome Back</h1>
                <p>Sign in to PriceCheck Admin Portal</p>
              </div>

              {error && (
                <div className="error-message">
                  <span>⚠️</span>
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="login-form">
                <div className="form-group">
                  <label htmlFor="usernameOrEmail">Email or Username</label>
                  <div className="input-wrapper">
                    <Mail size={20} className="input-icon" />
                    <input
                      id="usernameOrEmail"
                      type="text"
                      value={usernameOrEmail}
                      onChange={(e) => setUsernameOrEmail(e.target.value)}
                      placeholder="Enter your email or username"
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="input-wrapper">
                    <Lock size={20} className="input-icon" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="form-options">
                  <label className="remember-me">
                    <input type="checkbox" />
                    <span>Remember me</span>
                  </label>
                  <button 
                    type="button"
                    className="forgot-password"
                    onClick={() => console.log('Forgot password clicked')}
                  >
                    Forgot password?
                  </button>
                </div>

                <button 
                  type="submit" 
                  className="login-button" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              <div className="login-footer">
                <p>Protected by enterprise-grade security</p>
              </div>
            </>
          ) : (
            // OTP Verification Form
            <>
              <div className="login-header">
                <button className="back-button" onClick={handleBackToLogin} type="button">
                  <ArrowLeft size={20} />
                </button>
                <div className="logo-wrapper">
                  <div className="logo-icon otp-icon">
                    <KeyRound size={32} />
                  </div>
                </div>
                <h1>Verify Your Identity</h1>
                <p>Enter the 6-digit code sent to <strong>{emailHint}</strong></p>
              </div>

              {error && (
                <div className="error-message">
                  <span>⚠️</span>
                  <p>{error}</p>
                </div>
              )}

              <div className="otp-form">
                <div className="otp-inputs" onPaste={handleOTPPaste}>
                  {otpDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (otpInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(index, e)}
                      className="otp-input"
                      autoComplete="one-time-code"
                      disabled={verifying}
                    />
                  ))}
                </div>

                <button 
                  type="button"
                  className="login-button verify-button" 
                  onClick={() => handleVerifyOTP()}
                  disabled={verifying || otpDigits.some(d => d === '')}
                >
                  {verifying ? (
                    <>
                      <span className="spinner"></span>
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </button>

                <div className="resend-section">
                  <p className="resend-text">Didn't receive the code?</p>
                  <button 
                    type="button"
                    className="resend-button"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || resending}
                  >
                    {resending ? (
                      <>
                        <RefreshCw size={16} className="spinning" />
                        Sending...
                      </>
                    ) : resendCooldown > 0 ? (
                      `Resend in ${resendCooldown}s`
                    ) : (
                      <>
                        <RefreshCw size={16} />
                        Resend Code
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="login-footer">
                <p>Code expires in 10 minutes</p>
              </div>
            </>
          )}
        </div>

        <div className="login-info">
          <div className="info-content">
            <h2>PriceCheck Admin</h2>
            <p className="info-subtitle">Manage your e-commerce platform with powerful tools</p>
            
            <div className="features-list">
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <div className="feature-text">
                  <h4>Comprehensive Dashboard</h4>
                  <p>Monitor all metrics in real-time</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <div className="feature-text">
                  <h4>Easy Management</h4>
                  <p>Control products, users, and more</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <div className="feature-text">
                  <h4>Secure Access</h4>
                  <p>Two-factor authentication enabled</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
